import { useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import PortfolioPageLink from './PortfolioPageLink'
import './portfolio-hero.css'

const MEDIA = '/media/eagle/'
const MOTION_QUERY = '(min-width: 768px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)'

export default function PortfolioHero() {
  const heroRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const autoplayAttempted = useRef(false)
  const [allowMotion, setAllowMotion] = useState(false)
  const [inView, setInView] = useState(false)
  const [loadVideo, setLoadVideo] = useState(false)
  const [ready, setReady] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [hasVideoFrame, setHasVideoFrame] = useState(false)
  const [ended, setEnded] = useState(false)

  useEffect(() => {
    const preference = window.matchMedia(MOTION_QUERY)
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    const update = () => {
      const permitted = preference.matches && !connection?.saveData
      setAllowMotion(permitted)
      if (!permitted) {
        videoRef.current?.pause()
        setPlaying(false)
        setHasVideoFrame(false)
        setReady(false)
      }
    }
    update()
    preference.addEventListener('change', update)
    return () => preference.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const hero = heroRef.current
    if (!hero || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: .25 })
    observer.observe(hero)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!allowMotion || !inView || loadVideo) return
    // Let the responsive poster and page content arrive before fetching decorative video.
    const timer = window.setTimeout(() => setLoadVideo(true), 700)
    return () => window.clearTimeout(timer)
  }, [allowMotion, inView, loadVideo])

  useEffect(() => {
    if (!allowMotion || !inView) {
      videoRef.current?.pause()
      setPlaying(false)
      return
    }
    if (!ready || autoplayAttempted.current || document.hidden) return
    autoplayAttempted.current = true
    videoRef.current?.play().catch(() => setPlaying(false))
  }, [allowMotion, inView, ready])

  useEffect(() => {
    const pauseWhenHidden = () => {
      if (document.hidden) {
        videoRef.current?.pause()
        setPlaying(false)
      }
    }
    document.addEventListener('visibilitychange', pauseWhenHidden)
    return () => document.removeEventListener('visibilitychange', pauseWhenHidden)
  }, [])

  const toggleMotion = () => {
    const video = videoRef.current
    if (!video) return
    if (!video.paused) {
      video.pause()
      setPlaying(false)
      return
    }
    autoplayAttempted.current = true
    if (video.ended) video.currentTime = 0
    video.play().catch(() => setPlaying(false))
  }

  return (
    <section ref={heroRef} className="portfolio-hero" aria-labelledby="portfolio-title">
      <div className="portfolio-hero-art">
        <img
          src={MEDIA + 'hero-1440.webp'}
          srcSet={`${MEDIA}hero-768.webp 768w, ${MEDIA}hero-1440.webp 1440w`}
          sizes="(max-width: 767px) 145vw, (max-width: 1120px) 100vw, 1120px"
          width="1672"
          height="941"
          alt=""
          fetchPriority="high"
          decoding="async"
        />
        {allowMotion && loadVideo && (
          <video
            ref={videoRef}
            src={MEDIA + 'hero-flight.mp4'}
            className={hasVideoFrame ? 'has-frame' : ''}
            width="1280"
            height="720"
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            aria-hidden="true"
            onCanPlay={() => setReady(true)}
            onPlaying={() => { setPlaying(true); setEnded(false); setHasVideoFrame(true) }}
            onPause={() => setPlaying(false)}
            onEnded={() => { setPlaying(false); setEnded(true) }}
            onError={() => { setPlaying(false); setReady(false); setHasVideoFrame(false) }}
          />
        )}
        <PortfolioPageLink
          to="/about?white-breasted-sea-eagle"
          className="portfolio-hero-eagle-link"
          aria-label="Meet the white-breasted sea eagle"
        ><span className="sr-only">Meet the white-breasted sea eagle</span></PortfolioPageLink>
      </div>

      <div className="portfolio-hero-copy">
        <p className="portfolio-hero-status"><span aria-hidden="true" />Currently building</p>
        <h1 id="portfolio-title">Jax<span className="portfolio-hero-period">.</span></h1>
        <p className="portfolio-hero-tagline">BioScience <span aria-hidden="true">→</span><span className="sr-only">to</span> <span className="portfolio-hero-builder">Builder<span className="portfolio-hero-cursor" aria-hidden="true" /></span></p>
        <p className="portfolio-hero-intro">
          BioScience background. Now building software at a startup,
          creating useful tools, and documenting what I learn.
        </p>
        <div className="portfolio-hero-actions">
          <a className="portfolio-hero-primary" href="#building">Explore my work</a>
          <a className="portfolio-hero-secondary" href="#writing">Read my notes</a>
        </div>
      </div>

      <div className="portfolio-hero-footnote">
        <PortfolioPageLink to="/about?white-breasted-sea-eagle" className="portfolio-hero-meet">Meet the eagle</PortfolioPageLink>
        {allowMotion && ready && (
          <button
            type="button"
            className="portfolio-hero-motion"
            onClick={toggleMotion}
            aria-label={playing ? 'Pause eagle animation' : ended ? 'Replay eagle animation' : 'Play eagle animation'}
            title={playing ? 'Pause eagle animation' : ended ? 'Replay eagle animation' : 'Play eagle animation'}
          >
            {playing ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
          </button>
        )}
      </div>
    </section>
  )
}
